import { buildYowanCurrentUser } from './yowanCurrentUser';
import { logoutYowan, syncYowanCurrentState } from './yowanAuthClient';
import { readYowanStoredSnapshot } from './yowanAuthStorage';
import { readYowanAuthRuntimeState } from './yowanAuthRuntime';

const hasActiveBlogAuthorAccess = (access = null) => {
  if (!access) {
    return false;
  }

  if (Array.isArray(access)) {
    return access.some(hasActiveBlogAuthorAccess);
  }

  if (typeof access !== 'object') {
    return Boolean(access);
  }

  if (Array.isArray(access.items)) {
    return access.items.some(hasActiveBlogAuthorAccess);
  }

  if (Array.isArray(access.results)) {
    return access.results.some(hasActiveBlogAuthorAccess);
  }

  if (access.isActive === false || access.is_active === false) {
    return false;
  }

  return Boolean(
    access.isActive === true ||
      access.is_active === true ||
      access.id ||
      access.authSubjectId ||
      access.auth_subject_id ||
      access.loginEmail ||
      access.login_email ||
      access.email ||
      access.author
  );
};

const hasBlogAuthorAccessLabel = (labels = []) =>
  Array.isArray(labels) &&
  labels.some((label) => {
    const text = String(label || '');
    return text.includes('ブログ執筆者') || text.toLowerCase().includes('blog_author');
  });

export const readYowanAuthAdapter = () => {
  const storedSnapshot = readYowanStoredSnapshot();
  const runtimeState = readYowanAuthRuntimeState();
  const authSummary = runtimeState.authSummary || storedSnapshot.authSummary || null;
  const commonFlags = authSummary?.common_flags || authSummary?.commonFlags || {};
  const subject = authSummary?.subject || {};
  const accessMe = runtimeState.accessMe || {};
  const canAccessAdmin = Boolean(
    runtimeState.adminStatus?.can_access_admin ??
      runtimeState.adminStatus?.canAccessAdmin ??
      commonFlags.can_access_admin ??
      commonFlags.canAccessAdmin ??
      accessMe.can_access_admin ??
      accessMe.canAccessAdmin ??
      false
  );
  const canViewMyPage = Boolean(commonFlags.can_view_mypage ?? commonFlags.canViewMyPage ?? storedSnapshot.isAuthenticated);
  const canAccessRelatedSeminars = Boolean(
    accessMe.canAccessRelatedSeminars ?? accessMe.can_access_related_seminars ?? false
  );
  const canAccessRelatedClasses = Boolean(
    accessMe.canAccessRelatedClasses ?? accessMe.can_access_related_classes ?? false
  );
  const isInstructor = Boolean(accessMe.isInstructor ?? accessMe.is_instructor ?? false);
  const isOrganizer = Boolean(accessMe.isOrganizer ?? accessMe.is_organizer ?? false);
  const instructorAccess = accessMe.instructorAccess || accessMe.instructor_access || null;
  const organizerAccess = accessMe.organizerAccess || accessMe.organizer_access || null;
  const contentPermissions = accessMe.contentPermissions || accessMe.content_permissions || {};
  const blogContentPermissions =
    accessMe.blogContentPermissions ||
    accessMe.blog_content_permissions ||
    contentPermissions.blog ||
    {};
  const blogAuthorAccess = accessMe.blogAuthorAccess || accessMe.blog_author_access || null;
  const canAccessBlogAuthor = Boolean(
    hasActiveBlogAuthorAccess(blogAuthorAccess) ||
      hasBlogAuthorAccessLabel(accessMe.accessLabels || accessMe.access_labels)
  );
  const canAccessStakeholderSignal =
    accessMe.canAccessStakeholder ?? accessMe.can_access_stakeholder;
  const canAccessStakeholder = Boolean(
    canAccessStakeholderSignal ||
    canAccessRelatedSeminars ||
    canAccessRelatedClasses ||
    isInstructor ||
    isOrganizer ||
    instructorAccess ||
    organizerAccess
  );
  const permissions = [
    canAccessAdmin ? 'admin' : null,
    canAccessStakeholder ? 'stakeholder' : null,
    canAccessRelatedSeminars ? 'related-seminars' : null,
    canAccessRelatedClasses ? 'related-classes' : null,
    isInstructor ? 'instructor' : null,
    isOrganizer ? 'organizer' : null,
    canAccessBlogAuthor ? 'blog-author' : null,
  ].filter(Boolean);
  const email = runtimeState.authMe?.email ||
    subject.email ||
    instructorAccess?.loginEmail ||
    instructorAccess?.login_email ||
    organizerAccess?.loginEmail ||
    organizerAccess?.login_email ||
    blogAuthorAccess?.loginEmail ||
    blogAuthorAccess?.login_email ||
    blogAuthorAccess?.email ||
    null;
  const snapshot = {
    ...storedSnapshot,
    ...runtimeState,
    permissions,
    canAccessAdmin,
    canAccessStakeholder,
    canAccessRelatedSeminars,
    canAccessRelatedClasses,
    isInstructor,
    isOrganizer,
    instructorAccess,
    organizerAccess,
    contentPermissions,
    blogContentPermissions,
    blogAuthorAccess,
    canAccessBlogAuthor,
    canViewMyPage,
    authSummary,
    user: email
      ? {
          email,
          displayName: runtimeState.authMe?.display_name || runtimeState.authMe?.displayName || subject.display_name || subject.displayName || '',
        }
      : null,
  };
  const currentUser = buildYowanCurrentUser(snapshot);

  return {
    ...snapshot,
    currentUser,
    authState: currentUser.role,
  };
};

export const performYowanLogout = async () => {
  try {
    await logoutYowan();
  } catch (error) {
    return false;
  }

  return true;
};

export const syncYowanAuthFoundation = async () => {
  await syncYowanCurrentState();
  return readYowanAuthAdapter();
};
